import React, { useState, useEffect } from 'react';
import {
  Stack,
  Group,
  Text,
  Button,
  Table,
  Select,
  Badge,
  Divider,
  Alert,
  Box,
  ScrollArea
} from '@mantine/core';
import { IconAlertCircle, IconArrowsJoin } from '@tabler/icons-react';
import { useClient } from '../../context/ClientContext';

function MergeClients({ selectedClients, onClose }) {
  const { clients, mergeClients, findPotentialDuplicates } = useClient();
  const [targetClient, setTargetClient] = useState(null);
  const [fieldMappings, setFieldMappings] = useState({});
  const [mergedPreview, setMergedPreview] = useState(null);
  const [duplicates, setDuplicates] = useState([]);

  useEffect(() => {
    // Find potential duplicates when component mounts
    const selectedClientsData = clients.filter(c => selectedClients.includes(c.id));
    findPotentialDuplicates({
      firstName: selectedClientsData.map(c => c.firstName),
      lastName: selectedClientsData.map(c => c.lastName),
      phone: selectedClientsData.map(c => `${c.areaCode}${c.phoneNumber}`)
    }).then(setDuplicates);
  }, [clients, selectedClients, findPotentialDuplicates]);

  useEffect(() => {
    // Initialize field mappings when target client changes
    if (targetClient) {
      const initialMappings = {};
      const sourceClients = clients.filter(c => 
        selectedClients.includes(c.id) && c.id !== targetClient.id
      );

      sourceClients.forEach(client => {
        initialMappings[client.id] = {
          firstName: targetClient.id,
          lastName: targetClient.id,
          email: targetClient.id,
          phone: targetClient.id,
          gender: targetClient.id,
          pronouns: targetClient.id,
          clientNotes: targetClient.id,
          isOptedInReminder: targetClient.id,
          isOptedInMarketing: targetClient.id
        };
      });

      setFieldMappings(initialMappings);
    }
  }, [targetClient, clients, selectedClients]);

  useEffect(() => {
    // Update merged preview when field mappings change
    if (targetClient && Object.keys(fieldMappings).length > 0) {
      const sourceClients = clients.filter(c => 
        selectedClients.includes(c.id) && c.id !== targetClient.id
      );

      const preview = { ...targetClient };

      sourceClients.forEach(client => {
        const mapping = fieldMappings[client.id];
        Object.keys(mapping).forEach(field => {
          if (mapping[field] === client.id) {
            preview[field] = client[field];
          }
        });
      });

      setMergedPreview(preview);
    }
  }, [fieldMappings, targetClient, clients, selectedClients]);

  const handleMerge = async () => {
    if (!targetClient) return;

    const sourceClientIds = selectedClients.filter(id => id !== targetClient.id);
    await mergeClients(targetClient.id, sourceClientIds, fieldMappings);
    onClose();
  };

  const getFieldValue = (client, field) => {
    switch (field) {
      case 'phone':
        return `(${client.areaCode}) ${client.phoneNumber}`;
      case 'isOptedInReminder':
      case 'isOptedInMarketing':
        return client[field] ? 'Yes' : 'No';
      default:
        return client[field] || '-';
    }
  };

  return (
    <Stack spacing="md">
      <Alert icon={<IconAlertCircle size={16} />} color="yellow">
        Merging clients will combine their information and delete the source clients. This action cannot be undone.
      </Alert>

      <Group position="apart">
        <Text weight={500}>Select Target Client</Text>
        <Select
          placeholder="Choose target client"
          data={clients
            .filter(c => selectedClients.includes(c.id))
            .map(c => ({
              value: c.id,
              label: `${c.firstName} ${c.lastName} (${c.email || 'No email'})`
            }))
          }
          value={targetClient?.id}
          onChange={(value) => setTargetClient(clients.find(c => c.id === value))}
          style={{ width: 300 }}
        />
      </Group>

      {targetClient && (
        <ScrollArea style={{ height: 400 }}>
          <Table>
            <thead>
              <tr>
                <th>Field</th>
                <th>Target Client</th>
                {clients
                  .filter(c => selectedClients.includes(c.id) && c.id !== targetClient.id)
                  .map(client => (
                    <th key={client.id}>
                      {client.firstName} {client.lastName}
                    </th>
                  ))}
              </tr>
            </thead>
            <tbody>
              {['firstName', 'lastName', 'email', 'phone', 'gender', 'pronouns', 'clientNotes', 'isOptedInReminder', 'isOptedInMarketing'].map(field => (
                <tr key={field}>
                  <td style={{ textTransform: 'capitalize' }}>
                    {field.replace(/([A-Z])/g, ' $1').trim()}
                  </td>
                  <td>
                    <Badge color="blue">
                      {getFieldValue(targetClient, field)}
                    </Badge>
                  </td>
                  {clients
                    .filter(c => selectedClients.includes(c.id) && c.id !== targetClient.id)
                    .map(client => (
                      <td key={client.id}>
                        <Select
                          value={fieldMappings[client.id]?.[field]}
                          onChange={(value) => setFieldMappings(prev => ({
                            ...prev,
                            [client.id]: {
                              ...prev[client.id],
                              [field]: value
                            }
                          }))}
                          data={[
                            { value: targetClient.id, label: 'Use Target' },
                            { value: client.id, label: 'Use Source' }
                          ]}
                        />
                      </td>
                    ))}
                </tr>
              ))}
            </tbody>
          </Table>
        </ScrollArea>
      )}

      {mergedPreview && (
        <Box>
          <Divider my="md" />
          <Text weight={500} mb="md">Merged Preview</Text>
          <Stack spacing="xs">
            <Group>
              <Text weight={500} style={{ width: 120 }}>Name:</Text>
              <Text>{mergedPreview.firstName} {mergedPreview.lastName}</Text>
            </Group>
            <Group>
              <Text weight={500} style={{ width: 120 }}>Email:</Text>
              <Text>{mergedPreview.email || '-'}</Text>
            </Group>
            <Group>
              <Text weight={500} style={{ width: 120 }}>Phone:</Text>
              <Text>{getFieldValue(mergedPreview, 'phone')}</Text>
            </Group>
            <Group>
              <Text weight={500} style={{ width: 120 }}>Gender:</Text>
              <Text>{mergedPreview.gender || '-'}</Text>
            </Group>
            <Group>
              <Text weight={500} style={{ width: 120 }}>Pronouns:</Text>
              <Text>{mergedPreview.pronouns || '-'}</Text>
            </Group>
            <Group>
              <Text weight={500} style={{ width: 120 }}>Opt-in Status:</Text>
              <Group spacing="xs">
                <Badge color={mergedPreview.isOptedInReminder ? 'green' : 'red'}>
                  Reminder: {mergedPreview.isOptedInReminder ? 'Yes' : 'No'}
                </Badge>
                <Badge color={mergedPreview.isOptedInMarketing ? 'green' : 'red'}>
                  Marketing: {mergedPreview.isOptedInMarketing ? 'Yes' : 'No'}
                </Badge>
              </Group>
            </Group>
            {mergedPreview.clientNotes && (
              <Group>
                <Text weight={500} style={{ width: 120 }}>Notes:</Text>
                <Text>{mergedPreview.clientNotes}</Text>
              </Group>
            )}
          </Stack>
        </Box>
      )}

      <Group position="right" mt="md">
        <Button variant="default" onClick={onClose}>
          Cancel
        </Button>
        <Button
          color="violet"
          leftIcon={<IconArrowsJoin size={14} />}
          onClick={handleMerge}
          disabled={!targetClient}
        >
          Merge Clients
        </Button>
      </Group>
    </Stack>
  );
}

export default MergeClients; 